import { useWindowScrollHandler } from '@mnd-frontend/hooks';
import IconCheck from '@mnd-frontend/icons/components/Check';
import IconGlobe from '@mnd-frontend/icons/components/Globe';
import { CUBIC_BEZIER } from '@mnd-frontend/ui/shared/constants/animations';
import { KEYS } from '@mnd-frontend/ui/shared/constants/keypress';
import {
  BREAKPOINTS,
  RADIUS_XL,
  SPACING_0_5,
  SPACING_1,
  SPACING_2,
  SPACING_6,
  WEB_RED_REG,
  WEB_WHITE_REG,
} from '@mnd-frontend/ui/shared/tokens';
import { type KeyboardEvent, memo, type RefObject, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import type { CurrentLocaleDataType, LanguageLinksContextInterface } from '../../../types/locale';
import { locales } from '../../../utils/maps/locale';

// #region Styled components
const LanguageButton = styled.button<{ $hide: boolean }>`
  height: ${SPACING_6};
  width: ${SPACING_6};
  cursor: pointer;
  border: none;
  color: inherit;
  background-color: transparent;
  transform: ${({ $hide }) => ($hide ? 'scaleY(0)' : 'scaleY(1)')};
  opacity: ${({ $hide }) => ($hide ? 0 : 1)};
  transition: opacity 200ms ease-in-out, transform 50ms 200ms ease-in-out;
`;

const ContentWrapperLanguage = styled.div<{ $isOpen: boolean }>`
  --local-distance: 0;
  --local-clip-br-rounded: 0;

  background: ${WEB_WHITE_REG};
  position: fixed;
  top: var(--header-height, 3.75rem);
  right: var(--local-distance, 0);
  width: 12rem;
  height: 14rem;
  clip-path: xywh(0 0 100% 0 round 0 0 var(--local-clip-br-rounded) ${RADIUS_XL});
  transition: clip-path 200ms ${CUBIC_BEZIER};

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      clip-path: xywh(0 0 100% 100% round 0 0 var(--local-clip-br-rounded) ${RADIUS_XL});
      overflow-y: auto;
    `}

    @media (min-width: ${BREAKPOINTS.XL}px) {
      --local-distance: var(--i18n-x-distance, 0);
      --local-clip-br-rounded: ${RADIUS_XL};
    }
`;

const ListLanguages = styled.ul`  
  list-style: none;
  margin: 0;
  padding: 0 ${SPACING_2};
  font-weight: 600;

  .item {
    padding: ${SPACING_1} 0;

    &:not(:last-of-type) {
      margin-bottom: ${SPACING_0_5}
    }
  }
`;
// Using `next/link` causes an InvalidStateError, therefore using native `a` in this case.
const LocaleLink = styled.a<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING_1};
  color: ${({ $isActive }) => ($isActive ? WEB_RED_REG : 'inherit')};
`;
// #endregion

// #region Component
const NavLanguage = ({
  currentLocale,
  localePathsMap,
  buttonRef,
  hide = false,
  tabIndex,
}: CurrentLocaleDataType & {
  localePathsMap: LanguageLinksContextInterface;
  buttonRef?: RefObject<HTMLButtonElement>;
  hide?: boolean;
  tabIndex?: number;
}) => {
  const [isI18nOpen, setIsI18nOpen] = useState(false);

  const i18nNav = useMemo(
    () =>
      locales.map(item => {
        return item.locale === currentLocale.locale
          ? { ...item, path: localePathsMap[item.locale], isActive: true }
          : { ...item, path: localePathsMap[item.locale], isActive: false };
      }),
    [currentLocale.locale, localePathsMap],
  );

  useWindowScrollHandler(() => setIsI18nOpen(false));

  const handleOnKeyUp = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === KEYS.ESCAPE) {
      setIsI18nOpen(false);
    }
  };

  return (
    <>
      <LanguageButton
        type="button"
        ref={buttonRef}
        onClick={() => {
          setIsI18nOpen(prev => !prev);
        }}
        onKeyUp={handleOnKeyUp}
        $hide={hide}
        aria-label="Change language"
        aria-controls="language-list"
        aria-expanded={isI18nOpen}
        tabIndex={tabIndex}
        title="Change language"
      >
        <IconGlobe aria-hidden />
      </LanguageButton>
      <ContentWrapperLanguage $isOpen={isI18nOpen} id="language-list">
        <ListLanguages as="menu">
          {i18nNav.map(({ locale, label, path, isActive }) => (
            <li key={locale} className="item">
              <LocaleLink
                href={`/${locale}/${path}`}
                tabIndex={isI18nOpen ? 0 : -1}
                {...(isActive ? { 'aria-current': true } : undefined)}
                $isActive={isActive}
              >
                {isActive ? <IconCheck /> : <div style={{ width: '20px' }} aria-hidden />}
                {label}
              </LocaleLink>
            </li>
          ))}
        </ListLanguages>
      </ContentWrapperLanguage>
    </>
  );
};
// #endregion

export default memo(NavLanguage);
